import { useState, useCallback, useEffect, useRef } from "react";
import { searchApi, type SearchRow } from "@/services/api/search";
import { inputMethodManager } from "@/utils/pinyin";

export interface UseSearchPanelResult {
  keyword: string;
  setKeyword: (val: string) => void;
  rows: SearchRow[];
  isLoading: boolean;
  error: string | null;
  hasSearchResults: boolean;
  search: (kw?: string) => Promise<SearchRow[] | undefined>;
  clearResults: () => void;
}

export function useSearchPanel(
  initialKeyword: string = ""
): UseSearchPanelResult {
  const [keyword, setKeyword] = useState<string>(initialKeyword);
  const [rows, setRows] = useState<SearchRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearchResults, setHasSearchResults] = useState<boolean>(false);
  const abortRef = useRef<AbortController | null>(null);
  const cacheRef = useRef<Map<string, SearchRow[]>>(new Map());

  const search = useCallback(
    async (kw?: string) => {
      const q = (kw ?? keyword).trim();
      console.log("🚀 执行真正的搜索（用户触发）:", { q, keyword, kw });
      if (!q) return;
      setIsLoading(true);
      setError(null);
      try {
        // 缓存命中 - 避免返回缓存的空结果
        if (cacheRef.current.has(q)) {
          const cached = cacheRef.current.get(q) || [];
          // 只有非空结果才使用缓存
          if (cached.length > 0) {
            console.log("Cache hit for search:", cached);
            setRows(cached);
            setHasSearchResults(cached.length > 0);
            setIsLoading(false);
            return cached;
          }
        }

        // 取消上一请求
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        console.log("Calling searchApi.search for real search:", q);
        const res = await searchApi.search(q, controller.signal);
        console.log("searchApi.search real search result:", res);
        // 避免缓存空结果，只有当有数据时才缓存
        if (res.items && res.items.length > 0) {
          cacheRef.current.set(q, res.items);
        }
        setRows(res.items);
        setHasSearchResults(res.items.length > 0);
        return res.items;
      } catch (e: any) {
        // 忽略取消错误
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
          console.log("Real search request canceled");
          return;
        }
        console.error("Real search error:", e);
        setError(e?.message ?? "搜索失败");
      } finally {
        setIsLoading(false);
      }
    },
    [keyword]
  );

  // 监听关键字变化，仅用于获取搜索建议（不执行搜索）
  useEffect(() => {
    const q = keyword.trim();
    if (!q) {
      setRows([]);
      setHasSearchResults(false);
      return;
    }

    // 检查是否应该获取搜索建议
    const shouldTrigger = inputMethodManager.shouldTriggerSearch(q);

    if (!shouldTrigger) {
      // console.log('🚫 跳过获取建议 - 输入法状态或拼音输入未完成:', q)
      return;
    }

    // 只获取搜索建议，不执行真正的搜索
    // 优化防抖时间，提升响应速度
    let debounceTime = window.innerWidth >= 1024 ? 150 : 200;

    const t = setTimeout(() => {
      // 这里只获取建议，不触发搜索跳转
      fetchSuggestions(q);
    }, debounceTime);
    return () => clearTimeout(t);
  }, [keyword]);

  // 新增：获取搜索建议的函数
  const fetchSuggestions = useCallback(async (q: string) => {
    console.log("🔍 获取搜索建议:", q);
    setIsLoading(true);
    setError(null);
    try {
      // 缓存命中 - 避免返回缓存的空结果
      if (cacheRef.current.has(q)) {
        const cached = cacheRef.current.get(q) || [];
        // 只有非空结果才使用缓存
        if (cached.length > 0) {
          console.log("Cache hit for suggestions:", cached);
          setRows(cached);
          setHasSearchResults(cached.length > 0);
          setIsLoading(false);
          return cached;
        }
      }

      // 取消上一请求
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      console.log("Calling searchApi.search for suggestions:", q);
      const res = await searchApi.search(q, controller.signal);
      console.log("searchApi.search suggestions result:", res);
      // 避免缓存空结果，只有当有数据时才缓存
      if (res.items && res.items.length > 0) {
        cacheRef.current.set(q, res.items);
      }
      setRows(res.items);
      setHasSearchResults(res.items.length > 0);
      return res.items;
    } catch (e: any) {
      // 忽略取消错误
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        console.log("Suggestions request canceled");
        return;
      }
      console.error("Suggestions error:", e);
      setError(e?.message ?? "获取建议失败");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setRows([]);
    setHasSearchResults(false);
    setError(null);
  }, []);

  return {
    keyword,
    setKeyword,
    rows,
    isLoading,
    error,
    hasSearchResults,
    search,
    clearResults,
  };
}

export default useSearchPanel;
